import { useContext } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { expressMutation, useExpressQuery, getQueryKey } from '../../../../repository/react-query/useExpress';
import { NotificationContext } from '../../../../NotificationContext';
import { buildFieldFilter, getSendScheduleQuery } from '../../../../repository/queries';
import { isHauler, isProcessor } from '../../../../../utils';
import { getScheduleDateRange } from './common/utils';
import { useScheduleData } from './ScheduleDataContext';
import { buildExpressBulkQuery } from '../../../../repository/queries/builders';

const userKeys = ['processor_id', 'hauling_id', '_id'];
const filterUsers = (users) => users.filter((v) => v !== 'select_all');
const errorMessage = 'An error occurred updating the schedule. Please try again or contact support if the problem persists.';

export const getParams = ({ queryParams, dateField }) => {
    const { groupBy, fetchBy, start, end, range, order, search, fields, populate, selectedDay, ...filters } = queryParams;
    const where = { ...filters, deleted_at: { eq: null } };

    if (start && range && dateField) {
        const adjustedStart = range === 'day' ? (selectedDay || start).clone() : start;

        const { startDate, endDate } = getScheduleDateRange(adjustedStart, range);

        where[dateField] = { between: [startDate, endDate] };
    }
    Object.keys(where).forEach((key) => {
        let value = where[key];
        if (Array.isArray(value)) {
            if (userKeys.includes(key)) value = filterUsers(value);
            const hasNone = value.includes('none');
            const filtered = value.filter((v) => v !== 'none');
            if (hasNone && filtered.length === 0) {
                // Only "None" selected — match null/unset
                where[key] = { eq: null };
            } else if (hasNone) {
                // Both real values and "None" selected — no filter needed
                delete where[key];
            } else if (filtered.length === 0) {
                delete where[key];
            } else {
                where[key] = { inq: filtered };
            }
        }
    });

    // At least one search term must be present and it can't be the empty string
    const searchValid = search && Object.keys(search).length > 0 && Object.values(search).some((v) => v !== '');

    return {
        order,
        populate,
        where,
        fields: buildFieldFilter(fields),
        ...(searchValid && { filter: { search } }),
    };
};

const getRoutes = ({ queryParams }) => {
    const endpoint = '/routes';
    const params = getParams({ queryParams });
    return useExpressQuery({ endpoint, params });
};

export const useGetRoutes = ({ queryParams }) => {
    const queryFn = getRoutes({ queryParams });
    const queryKey = ['routes', getQueryKey(queryParams)];
    return useQuery({
        queryFn,
        queryKey,
    });
};

export const getListViewMilkScheduleSupplyItems = ({ queryParams }) => {
    const { groupBy } = queryParams;
    const endpoint = `/schedule-supply-item/list-view/${groupBy}`;
    const params = getParams({
        queryParams,
        dateField: queryParams.fetchBy || 'start_date',
    });
    return useExpressQuery({ endpoint, params });
};

export const useGetListViewMilkScheduleSupplyItems = ({ queryParams, enabled = true } = {}) => {
    const queryFn = getListViewMilkScheduleSupplyItems({ queryParams });
    const queryKey = ['list-view-supply-items', getQueryKey(queryParams)];
    return useQuery({ queryKey, queryFn, enabled });
};

const getListViewMilkScheduleDemandItem = ({ queryParams }) => {
    const { start, range, selectedDay, processor_id = [] } = queryParams;
    const endpoint = '/schedule-demand-item/list-view/day';
    const params = getParams({
        queryParams: { start, range, selectedDay, processor_id },
        dateField: 'date',
    });
    return useExpressQuery({ endpoint, params });
};

export const useGetListViewMilkScheduleDemandItems = ({ queryParams, enabled = true } = {}) => {
    const queryFn = getListViewMilkScheduleDemandItem({ queryParams });
    const queryKey = ['list-view-demand-items', getQueryKey(queryParams)];
    return useQuery({ queryKey, queryFn, enabled });
};

export const getScheduleSupplyItem = ({ queryParams }) => {
    const endpoint = '/schedule-supply-item';
    const { _ids, ...rest } = queryParams;
    const params = getParams({ queryParams: rest });

    // Default populate, can be overridden by queryParams.populate
    const defaultPopulate = {
        processor_id: ['name', 'license_number'],
        bill_to_processor_id: ['name', 'license_number'],
        hauling_id: ['name'],
        route_id: ['name'],
        route_session_id: ['status'],
        'autopilot_metadata.processor_match_id': ['name', 'full_name'],
    };

    params.populate = { ...defaultPopulate, ...params.populate };

    if (_ids?.length > 20) {
        params.where = { ...params.where, _id: { inq: _ids } };
        return () => expressMutation({ method: 'POST', endpoint, params, body: true, queryBuilder: buildExpressBulkQuery });
    }
    if (_ids?.length) params.where = { ...params.where, _id: { inq: _ids } };
    return useExpressQuery({ endpoint, params });
};

export const useGetScheduleSupplyItem = ({ queryParams, enabled } = {}) => {
    const queryFn = getScheduleSupplyItem({ queryParams });
    const queryKey = ['schedule-supply-items', getQueryKey(queryParams)];
    return useQuery({ queryKey, queryFn, enabled });
};

export const getScheduleDemandItem = ({ queryParams }) => {
    const endpoint = '/schedule-demand-item';
    const { _ids, search, hauling_id, ...rest } = queryParams;
    const params = getParams({ queryParams: rest });
    params.populate = { processor_id: ['name'] };
    if (_ids?.length) params.where = { ...params.where, _id: { inq: _ids } };
    return useExpressQuery({ endpoint, params });
};

export const useGetScheduleDemandItem = ({ queryParams, enabled } = {}) => {
    const queryFn = getScheduleDemandItem({ queryParams });
    const queryKey = ['schedule-demand-items', getQueryKey(queryParams)];
    return useQuery({ queryKey, queryFn, enabled });
};

const getListViewProcessorTotals = ({ queryParams, dynamicRange }) => {
    const { start, hauling_id, processor_id, priority_type, range } = queryParams;
    const dateRange = dynamicRange && range ? range : 'week';
    const { startDate, endDate } = getScheduleDateRange(start, dateRange);
    const endpoint = '/schedule-demand-item/list-view-totals/processor';

    const queryParamsObject = {
        _id: processor_id,
        hauling_id,
        ...(priority_type && { priority_type }),
    };

    const params = getParams({ queryParams: queryParamsObject });
    params.where = { ...params.where, start: startDate.toISOString(), end: endDate.toISOString() };
    return useExpressQuery({ endpoint, params });
};

const getListViewHaulerTotals = ({ queryParams }) => {
    const { start, hauling_id, processor_id } = queryParams;
    const { startDate, endDate } = getScheduleDateRange(start, 'week');
    const endpoint = '/schedule-demand-item/list-view-totals/transport';
    const params = getParams({ queryParams: { _id: hauling_id, processor_id } });
    params.where = { ...params.where, start: startDate.toISOString(), end: endDate.toISOString() };
    return useExpressQuery({ endpoint, params });
};

export const useGetListViewTotals = ({ queryParams, processorEnabled = true, haulerEnabled = true, dynamicRange = false } = {}) => {
    const processorQueryFn = getListViewProcessorTotals({ queryParams, dynamicRange });
    const processorQueryKey = ['list-view-schedule-totals', 'list-view-processor-totals', getQueryKey(queryParams)];
    const haulerQueryFn = getListViewHaulerTotals({ queryParams });
    const haulerQueryKey = ['list-view-schedule-totals', 'list-view-hauler-totals', getQueryKey(queryParams)];
    return useQueries({
        queries: [
            {
                queryKey: processorQueryKey,
                queryFn: processorQueryFn,
                // Disabled if user is processor
                enabled: processorEnabled && !isProcessor(),
            },
            {
                queryKey: haulerQueryKey,
                queryFn: haulerQueryFn,
                // Disabled if user is hauler
                enabled: haulerEnabled && !isHauler(),
            },
        ],
    });
};

const getUsers = ({ queryParams, path }) => {
    const endpoint = path ? `/users/${path}` : '/users';
    const fields = ['name', 'role', 'license_number', 'hauler_number', 'deleted_at'];
    const params = getParams({ queryParams: { ...queryParams, fields: queryParams.fields ?? fields } });
    return useExpressQuery({ endpoint, params });
};

const getUserGroups = () => {
    const endpoint = '/user-groups';
    const populate = { users: ['role', 'name', 'deleted_at'] };
    return useExpressQuery({ endpoint, params: { populate } });
};

export const useSelectUsers = ({ queryParams, userGroupsEnabled, usersKey = 'users', usersPath = null } = {}) => {
    const userGroupsQueryFn = getUserGroups();
    const userGroupsQueryKey = ['user-groups'];
    const usersQueryFn = getUsers({ queryParams, path: usersPath });
    const usersQueryKey = [usersKey, getQueryKey(queryParams)];
    return useQueries({
        queries: [
            {
                queryKey: userGroupsQueryKey,
                queryFn: userGroupsQueryFn,
                enabled: userGroupsEnabled,
            },
            {
                queryKey: usersQueryKey,
                queryFn: usersQueryFn,
            },
        ],
    });
};

export const useMutateScheduleSupplyItem = (handleSuccess) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: expressMutation,
        onMutate: ({ id, body }) => {
            queryClient.invalidateQueries(['copy-validation']);
            queryClient.invalidateQueries(['schedule-supply-items']);
            if (!id) return;
            const previousDataMap = [];

            // iterate over all supply item queries
            queryClient
                .getQueryCache()
                .findAll(['list-view-supply-items'])
                .forEach((query) => {
                    const { queryKey } = query;
                    const oldData = queryClient.getQueryData(queryKey);

                    if (oldData) {
                        // cached data organized per day
                        const newData = oldData.map((row) => {
                            // update item in any day with the corresponding id
                            const updatedItems = row?.items?.map((item) =>
                                item?._id === id
                                    ? {
                                          ...item,
                                          ...body,
                                      }
                                    : item
                            );

                            return {
                                ...row,
                                items: updatedItems,
                            };
                        });

                        // update cache with modified data
                        queryClient.setQueryData(queryKey, newData);
                    }
                });

            // return previous data for potential rollback
            return { previousDataMap };
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['list-view-schedule-totals']);
            queryClient.invalidateQueries(['auto-match-supply-items']);
            queryClient.invalidateQueries(['auto-match-preflight']);

            if (handleSuccess) {
                handleSuccess({ success: true });
            } else {
                showSnackbar({ severity: 'success', message: 'Success' });
            }
        },
        onError: () => {
            showSnackbar({ severity: 'error', message: errorMessage });
        },
    });
};

export const useMutateScheduleDemandItem = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    const { demandSSEStatus } = useScheduleData();
    return useMutation({
        mutationFn: expressMutation,
        onMutate: ({ id, body }) => {
            queryClient.invalidateQueries(['schedule-demand-items']);
            if (!id) return;
            const previousDataMap = [];

            // iterate over all supply item queries
            queryClient
                .getQueryCache()
                .findAll(['list-view-demand-items'])
                .forEach((query) => {
                    const { queryKey } = query;
                    const oldData = queryClient.getQueryData(queryKey);

                    if (oldData) {
                        // cached data organized per day
                        const newData = oldData.map((row) => {
                            // update item in any day with the corresponding id
                            const updatedItems = row?.items?.map((item) =>
                                item?._id === id
                                    ? {
                                          ...item,
                                          ...body,
                                      }
                                    : item
                            );

                            return {
                                ...row,
                                items: updatedItems,
                            };
                        });

                        // update cache with modified data
                        queryClient.setQueryData(queryKey, newData);
                    }
                });

            // return previous data for potential rollback
            return { previousDataMap };
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['list-view-schedule-totals']);
            queryClient.invalidateQueries(['list-view-supply-items']);
            if (demandSSEStatus !== 'connected') {
                queryClient.invalidateQueries(['list-view-demand-items']);
            }
            showSnackbar({ severity: 'success', message: 'Success' });
        },
        onError: () => {
            showSnackbar({ severity: 'error', message: errorMessage });
        },
    });
};

/**
 * @param {Parameters<typeof getSendScheduleQuery>[0]} data
 */
const sendSchedule = (data) => {
    return expressMutation({ method: 'PUT', options: { url: getSendScheduleQuery(data) } });
};

export const useMutateSendSchedule = () => {
    return useMutation({ mutationFn: sendSchedule });
};

export const useCopyItemsValidation = (body, enabled = true) => {
    const { showSnackbar } = useContext(NotificationContext);
    return useQuery({
        enabled,
        queryFn: () => expressMutation({ method: 'POST', endpoint: '/schedule-supply-item/validate-copy', body }),
        queryKey: ['copy-validation', getQueryKey(body)],
        onError: () => {
            showSnackbar({ severity: 'error', message: 'An error occurred validating the schedule items. Please try again.' });
        },
    });
};

export const useMergeScheduleSupplyItem = (handleSuccess) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: (body) => expressMutation({ method: 'POST', endpoint: '/schedule-supply-item/merge', body }),
        onMutate: async ({ sourceItemId, destinationItemId }) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries(['schedule-supply-items']);
            await queryClient.cancelQueries(['list-view-supply-items']);

            // Snapshot the previous value for potential rollback
            const previousScheduleItems = queryClient.getQueryData(['schedule-supply-items']);
            const previousListViewItems = queryClient.getQueryData(['list-view-supply-items']);

            // Return a context object with the snapshotted values
            return { previousScheduleItems, previousListViewItems, sourceItemId, destinationItemId };
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['schedule-supply-items']);
            queryClient.invalidateQueries(['list-view-supply-items']);
            queryClient.invalidateQueries(['list-view-schedule-totals']);

            if (handleSuccess) {
                handleSuccess({ success: true });
            } else {
                showSnackbar({ severity: 'success', message: 'Items merged successfully' });
            }
        },
        onError: (error, variables, context) => {
            // If we have context, rollback the optimistic updates
            if (context?.previousScheduleItems) {
                queryClient.setQueryData(['schedule-supply-items'], context.previousScheduleItems);
            }
            if (context?.previousListViewItems) {
                queryClient.setQueryData(['list-view-supply-items'], context.previousListViewItems);
            }

            showSnackbar({ severity: 'error', message: errorMessage });
        },
    });
};

export const useUpdateRouteAverages = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: (body) => expressMutation({ method: 'PATCH', endpoint: '/schedule-supply-item/route-averages', body }),
        onSuccess: (body) => {
            queryClient.invalidateQueries(['schedule-supply-items']);
            queryClient.invalidateQueries(['list-view-supply-items']);
            queryClient.invalidateQueries(['routes']);

            const scheduleMessage = body.schedule === 0 ? '' : body.schedule === 1 ? 'schedule item' : 'schedule items';
            const routeMessage = body.route === 0 ? '' : body.route === 1 ? 'route average' : 'route averages';
            showSnackbar({ severity: 'success', message: `Successfully updated ${scheduleMessage} ${!!scheduleMessage && !!routeMessage ? 'and' : ''} ${routeMessage}` });
        },
        onError: () => {
            showSnackbar({ severity: 'error', message: errorMessage });
        },
    });
};
